<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-2 gap-2">
      <div class="col-span-12 md:col-span-2 chart-card" style="height: 400px">
        <v-chart :option="option" autoresize />
      </div>
    </div>

    <ag-grid-vue
      :autoSizeStrategy="autoSizeStrategy"
      class="ag-theme-alpine mt-4 md:mt-0"
      :columnDefs="columnDefs"
      :rowData="rowData"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 85vh"
      :pagination="true"
      :paginationAutoPageSize="true"
      :defaultColDef="defaultColDef"
      :onGridReady="onGridReady"
    >
    </ag-grid-vue>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef, GridApi } from 'ag-grid-community'
import { AgGridVue } from 'ag-grid-vue3'
import { useRoute, useRouter } from 'vue-router'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { formatMoney } from '@/utils/money'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      name: 'Name',
      quantity: 'Quantity',
      lowStockInventory: 'Low Stock Inventory'
    },
    ar: {
      name: 'أسم المنتج',
      quantity: 'الكمية',
      lowStockInventory: 'المخزون القليل'
    }
  }
})

const autoSizeStrategy = ref({
    type: 'fitCellContents'
});

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')

const option = ref({
  title: {
    text: t('lowStockInventory'),
    left: 'left'
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'item',
    formatter: ({ seriesName, name, value }: any) => {
      const salary = value ? formatMoney(value, 'IQD') : ''
      return `${name}</br>${salary}`
    }
  },
  xAxis: {
    type: 'category',
    data: []
  },
  yAxis: {
    type: 'value',
  },
  dataZoom: [
    {
      id: 'dataZoomX',
      type: 'slider',
      xAxisIndex: 0,
      filterMode: 'empty',
      start: 0,
      end: 50,
      handleSize: 0,
    },
    {
      type: 'inside',
      id: 'insideX',
      xAxisIndex: 0,
      start: 0,
      end: 50,
      zoomOnMouseWheel: false,
      moveOnMouseMove: true,
      moveOnMouseWheel: true
    }
  ],
  grid: {
    containLabel: true,
    bottom: 80,
    left: 0,
  },
  series: [
    {
      name: t('lowStockInventory'),
      type: 'bar',
      barWidth: 50,
      label: {
        show: true,
        position: 'inside',
      },
      data: [],
      z: 100
    }
  ]
})

// Data
const columnDefs = ref<ColDef[]>([])
const rowData = ref<any[]>([])
const defaultColDef = ref<ColDef>({})
const searchTerm = ref('')
const selectedValue = ref('')
const gridApi = ref<GridApi | undefined>(undefined)

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()

onBeforeMount(async () => {
  defaultColDef.value = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 150
  }

  columnDefs.value = [
    {
      headerName: t('name'),
      field: 'name',
      checkboxSelection: true,
      headerCheckboxSelection: true
    },
    {
      headerName: t('quantity'),
      field: 'quantity',
      cellStyle: { direction: 'ltr !important', textAlign: 'center' },
     
    }
  ]
})

// Methods
async function updateRowData() {
  gridApi.value?.showLoadingOverlay()

  try {
    const data: any = await http.get<any[]>('/reports/inventory/low-stock-items')
    rowData.value = data
    
    option.value.series[0].data = data.map((i: any) => i.quantity)
    option.value.xAxis.data = data.map((i: any) => i.name)
  } catch (error) {
    alertModal.showDanger({
      title: error.title,
      body: error.body
    })
  }

  gridApi.value?.hideOverlay()
}

async function onGridReady(params: any) {
  gridApi.value = params.api
  gridApi.value?.sizeColumnsToFit()
  await updateRowData()
}
</script>
